"use client";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRouter } from "next/navigation";
import { platformSend } from "@/lib/api/browser";
import {
  isKycProfileComplete,
  isReadyForKycVerification,
  kycInitiateFailureReason,
  kycView,
  toSecureUrl,
  type KycInitiateFailure,
} from "@/lib/api/kyc";
import { useProfile } from "@/lib/api/profile";
import { useIdentity } from "@/lib/api/session";
import { useShell } from "@/components/shell/ShellContext";
import { useToasts } from "@/components/ui/Toast";

/* The whitelabel's KYC hooks (useKyc, useStartKycVerification,
   useVerifyIdentityAction), on Northstar's session and toasts. */

const PROFILE_DETAILS = "/account?tab=Settings&focus=profile-details";
const GENERIC_FAILURE = "We couldn't start identity verification. Please try again or contact support.";

/* The one source for KYC-driven interface: see kycView. */
export const useKyc = () => kycView(useIdentity().identity);

export const useStartKycVerification = ({
  onProfileIncomplete,
  onAlreadyVerified,
}: {
  onProfileIncomplete?: () => void;
  onAlreadyVerified?: () => void;
} = {}) => {
  const client = useQueryClient();
  const { push } = useToasts();

  const initiate = useMutation({
    mutationFn: async () =>
      (
        await platformSend<{ data: { reference: string; verificationUrl: string } }>("kyc/initiate", {
          redirectUrl: `${window.location.origin}/kyc/result`,
        })
      ).data,
    onSuccess: (session) => {
      const url = toSecureUrl(session.verificationUrl);

      if (!url) {
        push({ tone: "error", title: GENERIC_FAILURE });
        return;
      }

      window.location.assign(url);
    },
    onError: (error) => {
      const reason = kycInitiateFailureReason(error);

      if (!reason) {
        push({ tone: "error", title: GENERIC_FAILURE });
        return;
      }

      const handle = {
        ALREADY_VERIFIED: () => {
          void client.invalidateQueries({ queryKey: ["whoami"] });
          push({ tone: "success", title: "Your identity is already verified." });
          onAlreadyVerified?.();
        },
        VERIFICATION_ALREADY_PENDING: () => {
          void client.invalidateQueries({ queryKey: ["whoami"] });
          push({ tone: "info", title: "Your verification is still being processed.", body: "Please check back shortly." });
        },
        PROFILE_INCOMPLETE: () => {
          onProfileIncomplete?.();
        },
        KYC_NOT_CONFIGURED: () => {
          push({ tone: "error", title: "Identity verification isn't available right now.", body: "Please contact support." });
        },
        REDIRECT_URL_NOT_ALLOWED: () => {
          push({
            tone: "error",
            title: "We couldn't start identity verification from this page.",
            body: "Please contact support.",
          });
        },
      } satisfies Record<KycInitiateFailure, () => void>;

      handle[reason]();
    },
  });

  return { startVerification: () => initiate.mutate(), isPending: initiate.isPending };
};

/* Verify identity from anywhere: the profile has to carry the details and an
   email first, so a player missing either is sent to the Profile Details
   form with a note, and only a ready one is sent to the provider. */
export const useVerifyIdentityAction = ({
  onPrerequisiteMissing,
  onAlreadyVerified,
}: {
  onPrerequisiteMissing?: () => void;
  onAlreadyVerified?: () => void;
} = {}) => {
  const { isSignedIn } = useIdentity();
  const profile = useProfile(isSignedIn);
  const router = useRouter();
  const { push } = useToasts();

  const toProfileDetails = (message: string) => {
    onPrerequisiteMissing?.();
    push({ tone: "info", title: message });
    router.push(PROFILE_DETAILS);
  };

  const { startVerification, isPending } = useStartKycVerification({
    onProfileIncomplete: () => toProfileDetails("Please complete your profile details before verifying your identity."),
    onAlreadyVerified,
  });

  const verifyIdentity = () => {
    /* No profile read yet: let the platform decide, as the whitelabel does. */
    if (!profile.data) {
      startVerification();
      return;
    }

    if (isReadyForKycVerification(profile.data)) {
      startVerification();
      return;
    }

    toProfileDetails(
      isKycProfileComplete(profile.data)
        ? "Please add an email address before verifying your identity."
        : "Please complete your profile details before verifying your identity.",
    );
  };

  return { verifyIdentity, isPending: isPending || profile.isLoading };
};

/* A withdrawal refused with KYC_REQUIRED: the session is read again (the
   level may have moved) and the verification prompt opens. */
export const useHandleKycRequired = () => {
  const client = useQueryClient();
  const { openKyc } = useShell();

  return () => {
    void client.invalidateQueries({ queryKey: ["whoami"] });
    openKyc();
  };
};
